<template>
  <div class="jobs-filter-container">
    <b-row class="d-flex justify-content-between align-items-center mx-1">
      <div class="d-flex flex-row">
        <h4>
          <template v-if="loading">{{ $t('general.loading') }}</template>
          <template v-else>{{ $tc('manage.total-results', total) }}</template>
        </h4>
      </div>
      <div class="d-flex flex-row">
        <sort v-if="isLoggedIn" class="mr-4" i18n-prefix="jobs.orderBy" :options="orderByEnums || jobOrderByEnums"
          :value="order" @input="v => $emit('orderUpdated', v)"/>
        <i18n v-else path="manage.sort-by-order" tag="p" class="my-0 py-1 mr-2">
          <template v-slot:sortOrder><strong>{{ $t('jobs.orderBy.CREATED_DESC') }}</strong></template>
        </i18n>
        <toggle-filters class="py-1 px-3" v-model="showWhereFilters"/>
      </div>
    </b-row>

    <b-card class="horizontal-where mt-3" v-if="showWhereFilters">
      <slot name="filter-where">
        <filter-where orientation="horizontal" :whereCustomFields="whereCustomFields"
        :value="where" @input="v => $emit('whereUpdated', v)"/>
      </slot>
    </b-card>

    <loading-spinner class="my-5 text-center" v-if="loading"/>

    <template v-else-if="results && (layout === 'block' || layout === 'select-modal')">
      <template v-if="total">
        <b-row class="mt-3">
          <b-col class="mb-4" cols="12" v-for="{ entry } in results.edges" v-if="entry && entry.id" :key="entry.id">
            <slot name="job-list-item" v-bind="{ entry, where, layout, onSelectJob: e => $emit('selectJob', e) }">
              <job-list-item :job="entry" :where="where" :layout="layout" @selectJob="e => $emit('selectJob', e)" />
            </slot>
          </b-col>
        </b-row>
        <div v-if="!hidePagination && page && totalPages" class="filter-pagination">
          <p>{{ $t('manage.page-n-of-t', { page, totalPages }) }}</p>
          <b-pagination :value="page" @input="v => $emit('pageUpdated', v)" :per-page="limit" :total-rows="total"/>
        </div>
      </template>
      <div class="mt-3" v-else>
        <p class="text-center">{{ $tc('manage.total-results', 0) }}</p>
      </div>
    </template>

    <template v-else-if="results && layout === 'page'">
      <template v-if="total">
        <b-row class="mt-3 row-cols-1 row-cols-md-2">
          <b-col class="mb-4" cols="12" lg="6" v-for="{ entry } in results.edges" v-if="entry && entry.id" :key="entry.id">
            <slot name="job-list-item" v-bind="{ entry, where, layout }">
              <job-list-item :job="entry" :where="where"/>
            </slot>
          </b-col>
        </b-row>
        <div v-if="!hidePagination && page && totalPages" class="filter-pagination">
          <p>{{ $t('manage.page-n-of-t', { page, totalPages }) }}</p>
          <b-pagination :value="page" @input="v => $emit('pageUpdated', v)" :per-page="limit" :total-rows="total"/>
        </div>
      </template>
      <div class="mt-3" v-else>
        <p class="text-center">{{ $tc('manage.total-results', 0) }}</p>
      </div>
    </template>
  </div>
</template>

<script>
import LoadingSpinner from '@/components/misc/LoadingSpinner';
import JobListItem from '@/components/jobs/JobsListItem';
import FilterWhere from '@/components/jobs/FilterWhere';
import Sort from '@/components/misc/Sort';
import ToggleFilters from '@/components/misc/ToggleFilters';
import enumsMixin from '@/mixins/enums';
import { mapRoot } from '@/mixins/root';

export default {
  name: 'JobsFilterContainer',
  props: {
    layout: {
      type: String,
      default: () => 'block',
      validate: layout => [ 'block', 'page', 'select-modal' ].includes(layout),
    },
    loading: {
      type: Boolean,
      default: () => false,
    },
    hidePagination: {
      type: Boolean,
      default: () => false,
    },
    defaultShowFilters: {
      type: Boolean,
      default: () => false,
    },
    where: {
      type: Object,
      default: () => ({}),
    },
    whereCustomFields: {
      type: Object,
      default: () => ({}),
    },
    order: {
      type: String,
      default: () => null,
    },
    orderByEnums: {
      type: Object,
      default: () => null,
    },
    results: {
      type: Object,
      default: () => ({
        edges: [],
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
      }),
      validate: ({ edges }) => Array.isArray(edges),
    },
    selectedContactIDs: {
      type: Array,
      default: () => ([]),
    },
  },
  components: {
    Sort,
    ToggleFilters,
    FilterWhere,
    LoadingSpinner,
    JobListItem,
  },
  mixins: [
    enumsMixin,
  ],
  data() {
    return {
      showWhereFilters: this.defaultShowFilters === true,
    };
  },
  computed: {
    ...mapRoot([ 'isLoggedIn' ]),
    page() {
      const { page } = this.results || {};
      return page || 1;
    },
    limit() {
      const { limit } = this.results || {};
      return limit || 10;
    },
    total() {
      const { total } = this.results || {};
      return total || 0;
    },
    totalPages() {
      const { totalPages } = this.results || {};
      return totalPages || 1;
    },
  },
};
</script>

<style lang="scss">
.jobs-filter-container {

  .card.horizontal-where {
    margin-top: 10px;
    margin-bottom: 20px;
  }

  .filter-pagination {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .vertical-where {
    .vertical-sort {
      padding: 10px 20px 0 20px;

      label {
        font-weight: normal;
      }

      .custom-radio {
        margin-right: 0;
        margin-bottom: 10px;
      }
    }

    .results-tag {
      color: rgba(49, 55, 67, 0.5);
    }
  }

}
</style>



// WEBPACK FOOTER //
// src/components/jobs/FilterContainer.vue